import CircularProgress from '../../shared/components/CircularProgress/CircularProgress';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Alert from '../../shared/components/Alert/Alert';
import Menu from '../../shared/components/Menu/Menu';
import { MenuItem } from '../../shared/components/Menu/Menu';
import ArrowDownwardRoundedIcon from '@mui/icons-material/ArrowDownwardRounded';
import ArrowUpwardRoundedIcon from '@mui/icons-material/ArrowUpwardRounded';
import MoreHorizRoundedIcon from '@mui/icons-material/MoreHorizRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import Box from '../../shared/components/Box/Box';
import DataGrid from '../../shared/components/DataGrid/DataGrid';
import Typography from '../../shared/components/Typography/Typography';
import { getAllProjects } from './project.api';
import Input from '../../shared/components/Input/Input';
import Select from '../../shared/components/Select/Select';
import './ProjectPage/ProjectPage.scss';
import './projectList.scss';

const getValue = (project, ...keys) => keys.map((key) => project?.[key]).find((value) => value !== undefined && value !== null);
const normalize = (value) => String(value || '').trim().toLocaleLowerCase('fr-FR');
const closedStatuses = ['terminé', 'termine', 'closed', 'done', 'archivé', 'archive'];
const suspendedStatuses = ['suspendu', 'suspended', 'paused', 'bloqué', 'bloque'];

const formatDate = (value) => {
  if (!value) return 'Non renseignée';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString('fr-FR');
};

const formatBudget = (value) => {
  if (value === null || value === undefined || value === '') return 'Non renseigné';
  const amount = Number(value);
  if (Number.isNaN(amount)) return value;
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency', currency: 'EUR', maximumFractionDigits: 0,
  }).format(amount);
};

const getClientName = (project) => {
  const client = project?.Client || project?.client || project?.company;
  return getValue(client, 'socialReason', 'social_reason', 'name', 'legalName', 'legal_name')
    || getValue(project, 'clientName', 'client_name')
    || 'Client non renseigné';
};

const getProgress = (project) => {
  const value = Number(getValue(project, 'progressPercent', 'progress_percent', 'progress') || 0);
  return Number.isNaN(value) ? 0 : Math.min(100, Math.max(0, Math.round(value)));
};

const getStatusTone = (status) => {
  const value = normalize(status);
  if (closedStatuses.includes(value)) return 'complete';
  if (suspendedStatuses.includes(value)) return 'suspended';
  if (value.includes('cours') || value === 'active' || value === 'ongoing') return 'active';
  if (value.includes('étude') || value.includes('etude') || value.includes('conception')) return 'study';
  return 'neutral';
};

function ProjectList() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({ search: '', status: '', startDate: '', endDate: '' });
  const [sort, setSort] = useState({ field: 'endDate', direction: 'asc' });
  const [paginationModel, setPaginationModel] = useState({ pageSize: 10, page: 0 });
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuProject, setMenuProject] = useState(null);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        setLoading(true);
        const data = await getAllProjects();
        const projectList = Array.isArray(data) ? data : data.data || [];
        setProjects(projectList.map((project, index) => ({
          ...project,
          _gridId: project.id || project.projectId || `project-${index}`,
        })));
        setError(null);
      } catch (err) {
        setError(err.message || 'Une erreur est survenue lors du chargement des projets');
        setProjects([]);
      } finally {
        setLoading(false);
      }
    };
    fetchProjects();
  }, []);

  const statusOptions = useMemo(() => (
    Array.from(new Set(projects.map((project) => project.status).filter(Boolean))).sort()
  ), [projects]);

  const filteredProjects = useMemo(() => {
    const result = projects.filter((project) => {
      const searchable = [
        project.name, project.label, project.code, getClientName(project), project.city,
      ].map(normalize).join(' ');
      const start = getValue(project, 'startDate', 'start_date');
      const end = getValue(project, 'endDate', 'end_date');
      return searchable.includes(normalize(filters.search))
        && (!filters.status || project.status === filters.status)
        && (!filters.startDate || (start && new Date(start) >= new Date(filters.startDate)))
        && (!filters.endDate || (end && new Date(end) <= new Date(filters.endDate)));
    });

    return result.sort((left, right) => {
      const values = {
        name: [getValue(left, 'name', 'label'), getValue(right, 'name', 'label')],
        client: [getClientName(left), getClientName(right)],
        progress: [getProgress(left), getProgress(right)],
        budget: [Number(left.budget || 0), Number(right.budget || 0)],
        endDate: [new Date(getValue(left, 'endDate', 'end_date') || 8640000000000000).getTime(), new Date(getValue(right, 'endDate', 'end_date') || 8640000000000000).getTime()],
        status: [left.status, right.status],
      }[sort.field] || ['', ''];
      const comparison = typeof values[0] === 'number'
        ? values[0] - values[1]
        : String(values[0] || '').localeCompare(String(values[1] || ''), 'fr');
      return sort.direction === 'asc' ? comparison : -comparison;
    });
  }, [filters, projects, sort]);

  const metrics = useMemo(() => {
    const complete = projects.filter((project) => closedStatuses.includes(normalize(project.status))).length;
    const suspended = projects.filter((project) => suspendedStatuses.includes(normalize(project.status))).length;
    const budget = projects.reduce((sum, project) => sum + (Number(project.budget) || 0), 0);
    return { complete, suspended, active: Math.max(0, projects.length - complete - suspended), budget };
  }, [projects]);

  const resetFilters = () => setFilters({ search: '', status: '', startDate: '', endDate: '' });
  const hasFilters = Object.values(filters).some(Boolean);
  const updateFilter = (event) => {
    const { name, value } = event.target;
    setFilters((current) => ({ ...current, [name]: value }));
    setPaginationModel((current) => ({ ...current, page: 0 }));
  };
  const toggleSort = (field) => setSort((current) => ({
    field,
    direction: current.field === field && current.direction === 'asc' ? 'desc' : 'asc',
  }));
  const SortLabel = ({ field, children }) => (
    <button className="project-list-sort" type="button" onClick={() => toggleSort(field)}>
      {children}
      {sort.field === field && (sort.direction === 'asc'
        ? <ArrowUpwardRoundedIcon fontSize="inherit" />
        : <ArrowDownwardRoundedIcon fontSize="inherit" />)}
    </button>
  );
  const openProject = (project) => {
    const id = project?.id || project?.projectId;
    if (id) navigate(`/projets/${id}`);
  };
  const openMenu = (event, project) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setMenuProject(project);
  };
  const closeMenu = () => {
    setMenuAnchor(null);
    setMenuProject(null);
  };

  const columns = [
    {
      field: 'name', headerName: <SortLabel field="name">Projet</SortLabel>, width: 270, minWidth: 270,
      renderCell: ({ row }) => (
        <div className="project-list-identity">
          <strong>{getValue(row, 'name', 'label') || 'Projet sans nom'}</strong>
          <span>{[row.code, getClientName(row)].filter(Boolean).join(' · ')}</span>
        </div>
      ),
    },
    {
      field: 'progress', headerName: <SortLabel field="progress">Avancement</SortLabel>, width: 170, minWidth: 170,
      renderCell: ({ row }) => {
        const progress = getProgress(row);
        return <div className="project-list-progress"><div><span style={{ width: `${progress}%` }} /></div><strong>{progress} %</strong></div>;
      },
    },
    {
      field: 'budget', headerName: <SortLabel field="budget">Budget</SortLabel>, width: 150, minWidth: 150,
      renderCell: ({ row }) => <span className="project-list-value">{formatBudget(row.budget)}</span>,
    },
    {
      field: 'endDate', headerName: <SortLabel field="endDate">Échéance</SortLabel>, width: 145, minWidth: 145,
      renderCell: ({ row }) => <span className="project-list-value">{formatDate(getValue(row, 'endDate', 'end_date'))}</span>,
    },
    {
      field: 'status', headerName: <SortLabel field="status">Statut</SortLabel>, width: 150, minWidth: 150,
      renderCell: ({ row }) => <span className={`project-status project-status--${getStatusTone(row.status)}`}><i />{row.status || 'Non défini'}</span>,
    },
    {
      field: 'actions', headerName: '', width: 56, minWidth: 56,
      renderCell: ({ row }) => <button className="project-list-more" type="button" aria-label={`Actions pour ${row.name || 'le projet'}`} onClick={(event) => openMenu(event, row)}><MoreHorizRoundedIcon /></button>,
    },
  ];

  return (
    <Box className="page-container list-page project-list-page">
      <header className="project-list-header">
        <div>
          <span className="project-list-eyebrow">Production</span>
          <Typography variant="h4" component="h1">Projets</Typography>
          <p>Pilotez les opérations actives et accédez rapidement à leur espace de travail.</p>
        </div>
        <button className="basic-button active" type="button" onClick={() => navigate('/affaires')}>
          Créer depuis une affaire
        </button>
      </header>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <section className="project-list-metrics" aria-label="Synthèse des projets">
        <div><strong>{projects.length}</strong><span>Projets dans le portefeuille</span></div>
        <div><strong>{metrics.active}</strong><span>Projets actifs</span></div>
        <div><strong>{metrics.complete}</strong><span>Projets terminés</span></div>
        <div><strong>{formatBudget(metrics.budget)}</strong><span>Budget total du portefeuille</span></div>
      </section>

      <section className="project-list-filters" aria-label="Filtres des projets">
          <Input fullWidth label="Rechercher" name="search" value={filters.search} onChange={updateFilter} placeholder="Nom, partenaire, code…" />
          <Select fullWidth label="Statut" name="status" value={filters.status} onChange={updateFilter} options={[{ value: '', label: 'Tous les statuts' }, ...statusOptions.map((status) => ({ value: status, label: status }))]} />
          <Input fullWidth label="Début à partir du" type="date" name="startDate" value={filters.startDate} onChange={updateFilter} />
          <Input fullWidth label="Échéance jusqu’au" type="date" name="endDate" value={filters.endDate} onChange={updateFilter} />
          {hasFilters && <button className="project-list-reset" type="button" onClick={resetFilters}>Réinitialiser</button>}
      </section>

      <section className="project-list-panel">
        <header className="project-list-panel-header">
          <div><h2>Liste des projets</h2><p>{filteredProjects.length} résultat{filteredProjects.length !== 1 ? 's' : ''}</p></div>
          <span>Page {paginationModel.page + 1}</span>
        </header>

        {loading ? (
          <Box className="loading-state"><CircularProgress /></Box>
        ) : filteredProjects.length === 0 ? (
          <Box className="empty-state">
            <SearchRoundedIcon />
            <Typography variant="body1">Aucun projet ne correspond à ces critères.</Typography>
            {hasFilters && <button className="basic-button" type="button" onClick={resetFilters}>Effacer les filtres</button>}
          </Box>
        ) : (
          <DataGrid
            className="project-list-grid"
            rows={filteredProjects}
            columns={columns}
            pageSizeOptions={[10, 25, 50]}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            getRowId={(row) => row._gridId}
            onRowClick={({ row }) => openProject(row)}
          />
        )}
      </section>

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu}>
        <MenuItem onClick={() => { openProject(menuProject); closeMenu(); }}>Ouvrir le projet</MenuItem>
        <MenuItem onClick={() => { const id = menuProject?.id || menuProject?.projectId; if (id) navigate(`/projets/${id}/lda`); closeMenu(); }}>Liste des documents</MenuItem>
        <MenuItem onClick={() => { const id = menuProject?.id || menuProject?.projectId; if (id) navigate(`/projets/${id}/devis`); closeMenu(); }}>Devis liés</MenuItem>
      </Menu>
    </Box>
  );
}

export default ProjectList;
